// Age-banded, gender-specific point lookup for the 6 Uddyamaan fitness tests.
//
// Age bucket is computed against a FIXED program reference date, not the
// live current date -- the applicant DOB eligibility window (2012-07-01 to
// 2018-06-30, see UddyamaanApplyForm.jsx DOB_MIN/DOB_MAX in the main site
// repo) is itself anchored to this same reference date, so scoring bands
// must use it too or every applicant's age bucket would silently drift as
// time passes through the program cycle. Keep in sync with
// uk_module_admin_api/app/Library/UddyamaanFitnessScoring.php.
export const AGE_REFERENCE_DATE = '2026-07-01';

// Only male tables provided so far. Add a 'female' key with the same
// per-test/per-bucket shape once those tables are available -- no other
// code changes needed.
const TABLES = {
  male: {
    shuttle_run: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 18.00, points: 10 }, { max: 18.50, points: 8 }, { max: 19.00, points: 6 }, { max: 19.50, points: 4 }, { max: 20.00, points: 2 }],
        9:  [{ max: 17.50, points: 10 }, { max: 18.00, points: 8 }, { max: 18.50, points: 6 }, { max: 19.00, points: 4 }, { max: 19.50, points: 2 }],
        10: [{ max: 17.00, points: 10 }, { max: 17.50, points: 8 }, { max: 18.00, points: 6 }, { max: 18.50, points: 4 }, { max: 19.00, points: 2 }],
        11: [{ max: 16.50, points: 10 }, { max: 17.00, points: 8 }, { max: 17.50, points: 6 }, { max: 18.00, points: 4 }, { max: 18.50, points: 2 }],
        12: [{ max: 16.00, points: 10 }, { max: 16.50, points: 8 }, { max: 17.00, points: 6 }, { max: 17.50, points: 4 }, { max: 18.00, points: 2 }],
        13: [{ max: 15.50, points: 10 }, { max: 16.00, points: 8 }, { max: 16.50, points: 6 }, { max: 17.00, points: 4 }, { max: 17.50, points: 2 }],
      },
    },
    flying_run: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 4.50, points: 10 }, { max: 4.70, points: 8 }, { max: 4.90, points: 6 }, { max: 5.10, points: 4 }, { max: 5.30, points: 2 }],
        9:  [{ max: 4.20, points: 10 }, { max: 4.50, points: 8 }, { max: 4.70, points: 6 }, { max: 4.90, points: 4 }, { max: 5.10, points: 2 }],
        10: [{ max: 4.10, points: 10 }, { max: 4.30, points: 8 }, { max: 4.50, points: 6 }, { max: 4.70, points: 4 }, { max: 4.90, points: 2 }],
        11: [{ max: 4.00, points: 10 }, { max: 4.20, points: 8 }, { max: 4.40, points: 6 }, { max: 4.60, points: 4 }, { max: 4.80, points: 2 }],
        12: [{ max: 3.90, points: 10 }, { max: 4.10, points: 8 }, { max: 4.30, points: 6 }, { max: 4.50, points: 4 }, { max: 4.70, points: 2 }],
        13: [{ max: 3.60, points: 10 }, { max: 3.90, points: 8 }, { max: 4.10, points: 6 }, { max: 4.30, points: 4 }, { max: 4.50, points: 2 }],
      },
    },
    // Thresholds in total seconds (min*60 + sec).
    run_600m: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 200, points: 10 }, { max: 210, points: 8 }, { max: 220, points: 6 }, { max: 230, points: 4 }, { max: 240, points: 2 }],
        9:  [{ max: 195, points: 10 }, { max: 205, points: 8 }, { max: 215, points: 6 }, { max: 225, points: 4 }, { max: 235, points: 2 }],
        10: [{ max: 190, points: 10 }, { max: 200, points: 8 }, { max: 210, points: 6 }, { max: 220, points: 4 }, { max: 230, points: 2 }],
        11: [{ max: 180, points: 10 }, { max: 190, points: 8 }, { max: 200, points: 6 }, { max: 210, points: 4 }, { max: 220, points: 2 }],
        12: [{ max: 170, points: 10 }, { max: 180, points: 8 }, { max: 190, points: 6 }, { max: 200, points: 4 }, { max: 210, points: 2 }],
        13: [{ max: 160, points: 10 }, { max: 170, points: 8 }, { max: 180, points: 6 }, { max: 190, points: 4 }, { max: 200, points: 2 }],
      },
    },
    broad_jump: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 171, points: 10 }, { min: 161, points: 8 }, { min: 151, points: 6 }, { min: 141, points: 4 }, { min: 131, points: 2 }],
        9:  [{ min: 181, points: 10 }, { min: 171, points: 8 }, { min: 161, points: 6 }, { min: 151, points: 4 }, { min: 141, points: 2 }],
        10: [{ min: 191, points: 10 }, { min: 181, points: 8 }, { min: 171, points: 6 }, { min: 161, points: 4 }, { min: 151, points: 2 }],
        11: [{ min: 201, points: 10 }, { min: 191, points: 8 }, { min: 181, points: 6 }, { min: 171, points: 4 }, { min: 161, points: 2 }],
        12: [{ min: 211, points: 10 }, { min: 201, points: 8 }, { min: 191, points: 6 }, { min: 181, points: 4 }, { min: 171, points: 2 }],
        13: [{ min: 221, points: 10 }, { min: 211, points: 8 }, { min: 201, points: 6 }, { min: 191, points: 4 }, { min: 181, points: 2 }],
      },
    },
    // Thresholds in total cm (meters*100 + cm).
    medicine_ball: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 266, points: 10 }, { min: 256, points: 8 }, { min: 246, points: 6 }, { min: 236, points: 4 }, { min: 226, points: 2 }],
        9:  [{ min: 291, points: 10 }, { min: 281, points: 8 }, { min: 271, points: 6 }, { min: 261, points: 4 }, { min: 251, points: 2 }],
        10: [{ min: 341, points: 10 }, { min: 331, points: 8 }, { min: 321, points: 6 }, { min: 311, points: 4 }, { min: 301, points: 2 }],
        11: [{ min: 441, points: 10 }, { min: 431, points: 8 }, { min: 421, points: 6 }, { min: 411, points: 4 }, { min: 401, points: 2 }],
        12: [{ min: 541, points: 10 }, { min: 531, points: 8 }, { min: 521, points: 6 }, { min: 511, points: 4 }, { min: 501, points: 2 }],
        13: [{ min: 651, points: 10 }, { min: 641, points: 8 }, { min: 631, points: 6 }, { min: 621, points: 4 }, { min: 611, points: 2 }],
      },
    },
    forward_bend: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 8.01, points: 10 }, { min: 7.01, points: 8 }, { min: 6.01, points: 6 }, { min: 5.01, points: 4 }, { min: 4.01, points: 2 }],
        9:  [{ min: 9.01, points: 10 }, { min: 8.01, points: 8 }, { min: 7.01, points: 6 }, { min: 6.01, points: 4 }, { min: 5.01, points: 2 }],
        10: [{ min: 10.01, points: 10 }, { min: 9.01, points: 8 }, { min: 8.01, points: 6 }, { min: 7.01, points: 4 }, { min: 6.01, points: 2 }],
        11: [{ min: 11.01, points: 10 }, { min: 10.01, points: 8 }, { min: 9.01, points: 6 }, { min: 8.01, points: 4 }, { min: 7.01, points: 2 }],
        12: [{ min: 12.01, points: 10 }, { min: 11.01, points: 8 }, { min: 10.01, points: 6 }, { min: 9.01, points: 4 }, { min: 8.01, points: 2 }],
        13: [{ min: 13.01, points: 10 }, { min: 12.01, points: 8 }, { min: 11.01, points: 6 }, { min: 10.01, points: 4 }, { min: 9.01, points: 2 }],
      },
    },
  },
  female: {
    shuttle_run: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 18.90, points: 10 }, { max: 19.30, points: 8 }, { max: 19.70, points: 6 }, { max: 20.10, points: 4 }, { max: 20.50, points: 2 }],
        9:  [{ max: 18.80, points: 10 }, { max: 19.20, points: 8 }, { max: 19.60, points: 6 }, { max: 20.00, points: 4 }, { max: 20.40, points: 2 }],
        10: [{ max: 18.60, points: 10 }, { max: 19.00, points: 8 }, { max: 19.40, points: 6 }, { max: 19.80, points: 4 }, { max: 20.20, points: 2 }],
        11: [{ max: 17.80, points: 10 }, { max: 18.20, points: 8 }, { max: 18.60, points: 6 }, { max: 19.00, points: 4 }, { max: 19.40, points: 2 }],
        12: [{ max: 17.70, points: 10 }, { max: 18.10, points: 8 }, { max: 18.50, points: 6 }, { max: 18.90, points: 4 }, { max: 19.30, points: 2 }],
        13: [{ max: 17.60, points: 10 }, { max: 18.00, points: 8 }, { max: 18.40, points: 6 }, { max: 18.80, points: 4 }, { max: 19.20, points: 2 }],
      },
    },
    flying_run: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 4.60, points: 10 }, { max: 4.90, points: 8 }, { max: 5.20, points: 6 }, { max: 5.50, points: 4 }, { max: 5.80, points: 2 }],
        9:  [{ max: 4.40, points: 10 }, { max: 4.70, points: 8 }, { max: 5.00, points: 6 }, { max: 5.30, points: 4 }, { max: 5.60, points: 2 }],
        10: [{ max: 4.20, points: 10 }, { max: 4.50, points: 8 }, { max: 4.80, points: 6 }, { max: 5.10, points: 4 }, { max: 5.40, points: 2 }],
        11: [{ max: 4.10, points: 10 }, { max: 4.30, points: 8 }, { max: 4.60, points: 6 }, { max: 4.90, points: 4 }, { max: 5.20, points: 2 }],
        12: [{ max: 3.90, points: 10 }, { max: 4.20, points: 8 }, { max: 4.50, points: 6 }, { max: 4.80, points: 4 }, { max: 5.10, points: 2 }],
        13: [{ max: 3.80, points: 10 }, { max: 4.10, points: 8 }, { max: 4.40, points: 6 }, { max: 4.70, points: 4 }, { max: 5.00, points: 2 }],
      },
    },
    run_600m: {
      direction: 'lower_is_better',
      buckets: {
        8:  [{ max: 240, points: 10 }, { max: 250, points: 8 }, { max: 260, points: 6 }, { max: 270, points: 4 }, { max: 280, points: 2 }],
        9:  [{ max: 220, points: 10 }, { max: 230, points: 8 }, { max: 240, points: 6 }, { max: 250, points: 4 }, { max: 260, points: 2 }],
        10: [{ max: 200, points: 10 }, { max: 210, points: 8 }, { max: 220, points: 6 }, { max: 230, points: 4 }, { max: 240, points: 2 }],
        11: [{ max: 190, points: 10 }, { max: 200, points: 8 }, { max: 210, points: 6 }, { max: 220, points: 4 }, { max: 230, points: 2 }],
        12: [{ max: 180, points: 10 }, { max: 190, points: 8 }, { max: 200, points: 6 }, { max: 210, points: 4 }, { max: 220, points: 2 }],
        13: [{ max: 170, points: 10 }, { max: 180, points: 8 }, { max: 190, points: 6 }, { max: 200, points: 4 }, { max: 210, points: 2 }],
      },
    },
    broad_jump: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 147, points: 10 }, { min: 139, points: 8 }, { min: 131, points: 6 }, { min: 123, points: 4 }, { min: 115, points: 2 }],
        9:  [{ min: 155, points: 10 }, { min: 147, points: 8 }, { min: 139, points: 6 }, { min: 131, points: 4 }, { min: 123, points: 2 }],
        10: [{ min: 158, points: 10 }, { min: 152, points: 8 }, { min: 144, points: 6 }, { min: 137, points: 4 }, { min: 129, points: 2 }],
        11: [{ min: 168, points: 10 }, { min: 160, points: 8 }, { min: 152, points: 6 }, { min: 144, points: 4 }, { min: 136, points: 2 }],
        12: [{ min: 178, points: 10 }, { min: 170, points: 8 }, { min: 162, points: 6 }, { min: 154, points: 4 }, { min: 146, points: 2 }],
        13: [{ min: 188, points: 10 }, { min: 180, points: 8 }, { min: 172, points: 6 }, { min: 164, points: 4 }, { min: 156, points: 2 }],
      },
    },
    medicine_ball: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 240, points: 10 }, { min: 225, points: 8 }, { min: 210, points: 6 }, { min: 195, points: 4 }, { min: 180, points: 2 }],
        9:  [{ min: 270, points: 10 }, { min: 253, points: 8 }, { min: 236, points: 6 }, { min: 219, points: 4 }, { min: 202, points: 2 }],
        10: [{ min: 285, points: 10 }, { min: 269, points: 8 }, { min: 253, points: 6 }, { min: 237, points: 4 }, { min: 221, points: 2 }],
        11: [{ min: 491, points: 10 }, { min: 468, points: 8 }, { min: 445, points: 6 }, { min: 423, points: 4 }, { min: 400, points: 2 }],
        12: [{ min: 552, points: 10 }, { min: 529, points: 8 }, { min: 506, points: 6 }, { min: 483, points: 4 }, { min: 460, points: 2 }],
        13: [{ min: 596, points: 10 }, { min: 572, points: 8 }, { min: 548, points: 6 }, { min: 524, points: 4 }, { min: 500, points: 2 }],
      },
    },
    forward_bend: {
      direction: 'higher_is_better',
      buckets: {
        8:  [{ min: 10.01, points: 10 }, { min: 9.01, points: 8 }, { min: 8.01, points: 6 }, { min: 6.01, points: 4 }, { min: 4.00, points: 2 }],
        9:  [{ min: 12.51, points: 10 }, { min: 11.01, points: 8 }, { min: 9.01, points: 6 }, { min: 7.01, points: 4 }, { min: 5.00, points: 2 }],
        10: [{ min: 12.51, points: 10 }, { min: 12.01, points: 8 }, { min: 10.01, points: 6 }, { min: 8.01, points: 4 }, { min: 6.00, points: 2 }],
        11: [{ min: 14.01, points: 10 }, { min: 12.51, points: 8 }, { min: 11.01, points: 6 }, { min: 9.01, points: 4 }, { min: 7.00, points: 2 }],
        12: [{ min: 15.01, points: 10 }, { min: 13.51, points: 8 }, { min: 12.01, points: 6 }, { min: 9.51, points: 4 }, { min: 8.00, points: 2 }],
        13: [{ min: 16.01, points: 10 }, { min: 14.51, points: 8 }, { min: 13.01, points: 6 }, { min: 11.01, points: 4 }, { min: 9.00, points: 2 }],
      },
    },
  },
};

export function ageBucket(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  const ref = new Date(AGE_REFERENCE_DATE);
  if (isNaN(birth.getTime())) return null;
  let age = ref.getFullYear() - birth.getFullYear();
  const monthDiff = ref.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && ref.getDate() < birth.getDate())) age -= 1;
  return age >= 8 && age <= 13 ? age : null;
}

function pointsFromBands(bands, direction, value) {
  if (value === null || value === undefined || Number.isNaN(value)) return 0;
  for (const band of bands) {
    if (direction === 'lower_is_better' && value <= band.max) return band.points;
    if (direction === 'higher_is_better' && value >= band.min) return band.points;
  }
  return 0;
}

const toNumberOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

// Computes points for all 6 tests from raw measurement form values + gender/dob.
// `raw` uses the same field names as AssessmentFormModal's form state.
export function scoreAllTests(gender, dob, raw) {
  const bucket = ageBucket(dob);
  const genderTables = TABLES[gender] || null;

  const score = (test, value) => {
    if (bucket === null || genderTables === null) return '';
    const t = genderTables[test];
    if (!t) return '';
    const bands = t.buckets[bucket];
    if (!bands) return '';
    return pointsFromBands(bands, t.direction, value);
  };

  const shuttleRun = toNumberOrNull(raw.shuttle_run_seconds);
  const flyingRun = toNumberOrNull(raw.flying_run_seconds);
  const run600mMin = toNumberOrNull(raw.run_600m_minutes);
  const run600mSec = toNumberOrNull(raw.run_600m_seconds);
  const run600mTotalSec = (run600mMin !== null || run600mSec !== null)
    ? (run600mMin || 0) * 60 + (run600mSec || 0)
    : null;
  const broadJump = toNumberOrNull(raw.broad_jump_cm);
  const mbMeters = toNumberOrNull(raw.medicine_ball_meters);
  const mbCm = toNumberOrNull(raw.medicine_ball_cm);
  const mbTotalCm = (mbMeters !== null || mbCm !== null) ? (mbMeters || 0) * 100 + (mbCm || 0) : null;
  const forwardBend = toNumberOrNull(raw.forward_bend_cm);

  return {
    shuttle_run_points: score('shuttle_run', shuttleRun),
    flying_run_points: score('flying_run', flyingRun),
    run_600m_points: score('run_600m', run600mTotalSec),
    broad_jump_points: score('broad_jump', broadJump),
    medicine_ball_points: score('medicine_ball', mbTotalCm),
    forward_bend_points: score('forward_bend', forwardBend),
  };
}
